import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PropertyAnalysisForm, PropertyAnalysisFormData } from '../components/forms/PropertyAnalysisForm';
import { useAuth } from '../contexts/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { NavigationHeader } from '../components/ui/NavigationHeader';
import { AnalysisResultsChart } from '../components/ui/AnalysisResultsChart';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { apiClient, InvestmentAnalysisRequest, InvestmentAnalysisResponse, PropertyResponse } from '../services/api';

interface AnalysisResult {
  analysisId: string;
  analysisType: 'buy-hold' | 'flip' | 'brrr';
  propertyAddress: string;
  results: {
    // Buy & Hold Results
    monthlyRent?: number;
    monthlyExpenses?: number;
    monthlyCashFlow?: number;
    annualCashFlow?: number;
    cashOnCashReturn?: number;
    capRate?: number;
    totalCashInvested?: number;
    
    // Flip Results
    totalProjectCost?: number;
    expectedProfit?: number;
    profitMargin?: number;
    roi?: number;
    
    // BRRR Results
    totalInvestment?: number;
    refinanceAmount?: number;
    cashRecovered?: number;
    cashLeftIn?: number;
    infiniteReturn?: boolean;
  };
  createdAt: string;
}

export const PropertyAnalysis: React.FC = () => {
  const { user } = useAuth();
  const { trackUserAction, trackError } = useAnalytics();
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [existingProperty, setExistingProperty] = useState<PropertyResponse | null>(null);
  const [loadingProperty, setLoadingProperty] = useState(false);
  
  const isEditMode = Boolean(propertyId);

  // Load existing property data when in edit mode
  useEffect(() => {
    if (isEditMode && propertyId) {
      loadExistingProperty();
    }
  }, [propertyId, isEditMode]);

  const loadExistingProperty = async () => {
    if (!propertyId) return;
    
    try {
      setLoadingProperty(true);
      const property = await apiClient.getUserProperty(propertyId);
      setExistingProperty(property);
    } catch (err) {
      console.error('Failed to load existing property:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load property';
      setError(errorMessage);
    } finally {
      setLoadingProperty(false);
    }
  };

  const handleFormSubmit = async (formData: PropertyAnalysisFormData) => {
    if (!user) {
      setError('You must be logged in to analyze properties');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // Track analysis submission
    trackUserAction('property_analysis_started', {
      analysis_type: formData.analysisType,
      property_address: formData.address,
      purchase_price: formData.purchasePrice
    });

    try {
      // Convert form data to API request format
      const apiRequest: InvestmentAnalysisRequest = {
        analysis_type: formData.analysisType === 'buy-hold' ? 'buy-hold' : 
                      formData.analysisType === 'flip' ? 'flip' : 'brrr',
        property_data: {
          property_address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          purchase_price: formData.purchasePrice,
          down_payment: formData.downPayment,
          loan_amount: formData.loanAmount,
          interest_rate: formData.interestRate,
          loan_term: formData.loanTerm,
          annual_taxes: formData.annualPropertyTaxes,
          annual_insurance: formData.annualInsurance,
          monthly_hoa_fees: formData.monthlyHoaFees || 0,
          renovation_costs: {
            roof: formData.roofCosts || 0,
            flooring: formData.flooringCosts || 0,
            painting: formData.paintingCosts || 0,
            kitchen: formData.kitchenCosts || 0,
            bathroom: formData.bathroomCosts || 0,
            hvac: formData.hvacCosts || 0,
            electrical: formData.electricalCosts || 0,
            plumbing: formData.plumbingCosts || 0,
            general: formData.generalFixUpCosts || 0
          },
          expected_monthly_rent: formData.expectedRentPerMonth,
          after_repair_value: formData.afterRepairValue,
          selling_costs: formData.sellingCosts,
          holding_period_months: formData.holdingPeriodMonths
        },
        market_assumptions: {
          vacancy_rate: formData.vacancyRate || 5,
          maintenance_rate: formData.maintenanceRate || 10,
          management_rate: formData.managementRate || 8
        }
      };

      // Call Lambda function via API Gateway
      const apiResponse: InvestmentAnalysisResponse = await apiClient.runInvestmentAnalysis(apiRequest);
      
      // Convert API response to frontend format
      const result: AnalysisResult = {
        analysisId: apiResponse.analysis_id,
        analysisType: formData.analysisType,
        propertyAddress: formData.address, // Use form data since API doesn't return this
        results: {
          // Buy & Hold Results - map from nested response structure
          monthlyRent: apiResponse.results.monthly_analysis?.gross_monthly_income,
          monthlyExpenses: apiResponse.results.monthly_analysis?.total_expenses,
          monthlyCashFlow: apiResponse.results.monthly_analysis?.net_cash_flow,
          annualCashFlow: apiResponse.results.annual_analysis?.annual_cash_flow,
          cashOnCashReturn: apiResponse.results.investment_returns?.cash_on_cash_return,
          capRate: apiResponse.results.investment_returns?.cap_rate,
          totalCashInvested: apiResponse.results.property_metrics?.total_cash_invested,
          
          // Flip Results - map from actual response structure
          totalProjectCost: apiResponse.results.project_costs?.total_project_cost,
          expectedProfit: apiResponse.results.flip_analysis?.net_profit,
          profitMargin: apiResponse.results.flip_analysis?.profit_margin,
          roi: apiResponse.results.investment_returns?.roi,
          
          // BRRR Results (for future brrr implementation)
          totalInvestment: apiResponse.results.initial_investment?.total_cash_invested,
          refinanceAmount: apiResponse.results.refinance_analysis?.new_loan_amount,
          cashRecovered: apiResponse.results.refinance_analysis?.cash_recovered,
          cashLeftIn: apiResponse.results.refinance_analysis?.cash_left_in_deal,
          infiniteReturn: apiResponse.results.investment_returns?.infinite_return
        },
        createdAt: apiResponse.created_at
      };

      setAnalysisResult(result);

      // Track successful analysis completion
      trackUserAction('property_analysis_completed', {
        analysis_id: result.analysisId,
        analysis_type: result.analysisType,
        property_address: result.propertyAddress,
        cash_on_cash_return: result.results.cashOnCashReturn,
        cap_rate: result.results.capRate,
        monthly_cash_flow: result.results.monthlyCashFlow
      });
      
      // Save or update property data to backend
      try {
        const propertyData = {
          property_address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode,
          purchase_price: formData.purchasePrice,
          down_payment: formData.downPayment,
          loan_amount: formData.loanAmount,
          interest_rate: formData.interestRate,
          loan_term: formData.loanTerm,
          annual_taxes: formData.annualPropertyTaxes,
          annual_insurance: formData.annualInsurance,
          monthly_hoa_fees: formData.monthlyHoaFees,
          renovation_costs: {
            roof: formData.roofCosts || 0,
            flooring: formData.flooringCosts || 0,
            painting: formData.paintingCosts || 0,
            kitchen: formData.kitchenCosts || 0,
            bathroom: formData.bathroomCosts || 0,
            hvac: formData.hvacCosts || 0,
            electrical: formData.electricalCosts || 0,
            plumbing: formData.plumbingCosts || 0,
            general: formData.generalFixUpCosts || 0
          },
          expected_monthly_rent: formData.expectedRentPerMonth,
          after_repair_value: formData.afterRepairValue,
          selling_costs: formData.sellingCosts,
          vacancy_rate: formData.vacancyRate,
          maintenance_rate: formData.maintenanceRate,
          management_rate: formData.managementRate
        };

        if (isEditMode && propertyId) {
          await apiClient.updateUserProperty(propertyId, propertyData);
          // After successful update, redirect to property details
          setTimeout(() => {
            navigate(`/property/${propertyId}`);
          }, 2000);
        } else {
          await apiClient.createUserProperty(propertyData);
        }
      } catch (saveError) {
        console.warn('Property analysis completed but failed to save property data:', saveError);
        // Don't show error to user since analysis was successful
      }
      
    } catch (err) {
      console.error('Analysis calculation error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to calculate analysis. Please try again.';
      setError(errorMessage);

      // Track analysis error
      trackError('property_analysis_failed', errorMessage, {
        analysis_type: formData.analysisType,
        property_address: formData.address,
        purchase_price: formData.purchasePrice
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const _calculateAnalysis = (data: PropertyAnalysisFormData): AnalysisResult => {
    const baseResult = {
      analysisId: `analysis_${Date.now()}`,
      analysisType: data.analysisType,
      propertyAddress: `${data.address}, ${data.city}, ${data.state} ${data.zipCode}`,
      createdAt: new Date().toISOString(),
      results: {} as AnalysisResult['results']
    };

    // Calculate monthly mortgage payment
    const monthlyMortgage = calculateMonthlyPayment(
      data.loanAmount,
      data.interestRate,
      data.loanTerm
    );

    // Calculate total renovation costs
    const totalRenovationCosts = [
      data.roofCosts || 0,
      data.flooringCosts || 0,
      data.paintingCosts || 0,
      data.kitchenCosts || 0,
      data.bathroomCosts || 0,
      data.hvacCosts || 0,
      data.electricalCosts || 0,
      data.plumbingCosts || 0,
      data.generalFixUpCosts || 0,
    ].reduce((sum, cost) => sum + cost, 0);

    const totalCashInvested = data.downPayment + totalRenovationCosts;

    if (data.analysisType === 'buy-hold') {
      const monthlyRent = data.expectedRentPerMonth || 0;
      const monthlyPropertyTaxes = data.annualPropertyTaxes / 12;
      const monthlyInsurance = data.annualInsurance / 12;
      const monthlyHoa = data.monthlyHoaFees || 0;
      
      // Calculate vacancy, maintenance, and management costs
      const vacancyLoss = monthlyRent * ((data.vacancyRate || 0) / 100);
      const maintenanceCost = monthlyRent * ((data.maintenanceRate || 0) / 100);
      const managementCost = monthlyRent * ((data.managementRate || 0) / 100);
      
      const monthlyExpenses = monthlyMortgage + monthlyPropertyTaxes + monthlyInsurance + 
                             monthlyHoa + vacancyLoss + maintenanceCost + managementCost;
      const monthlyCashFlow = monthlyRent - monthlyExpenses;
      const annualCashFlow = monthlyCashFlow * 12;
      
      const cashOnCashReturn = totalCashInvested > 0 ? (annualCashFlow / totalCashInvested) * 100 : 0;
      const capRate = data.purchasePrice > 0 ? ((monthlyRent * 12 - (monthlyPropertyTaxes + monthlyInsurance + maintenanceCost) * 12) / data.purchasePrice) * 100 : 0;

      baseResult.results = {
        monthlyRent,
        monthlyExpenses,
        monthlyCashFlow,
        annualCashFlow,
        cashOnCashReturn,
        capRate,
        totalCashInvested
      };
    }

    if (data.analysisType === 'flip') {
      const totalProjectCost = data.purchasePrice + totalRenovationCosts + (data.sellingCosts || 0);
      const expectedProfit = (data.afterRepairValue || 0) - totalProjectCost;
      const profitMargin = data.afterRepairValue ? (expectedProfit / data.afterRepairValue) * 100 : 0;
      const roi = totalCashInvested > 0 ? (expectedProfit / totalCashInvested) * 100 : 0;

      baseResult.results = {
        totalProjectCost,
        expectedProfit,
        profitMargin,
        roi,
        totalCashInvested
      };
    }

    if (data.analysisType === 'brrr') {
      const totalInvestment = totalCashInvested;
      const refinanceAmount = (data.afterRepairValue || 0) * 0.75; // Assume 75% LTV on refinance
      const cashRecovered = Math.min(refinanceAmount - data.loanAmount, totalInvestment);
      const cashLeftIn = totalInvestment - cashRecovered;
      const infiniteReturn = cashLeftIn <= 0;

      baseResult.results = {
        totalInvestment,
        refinanceAmount,
        cashRecovered,
        cashLeftIn,
        infiniteReturn
      };
    }

    return baseResult;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">Please log in to access the property analysis tool.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />
      
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Property Analysis' : 'Property Investment Analysis'}
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            {isEditMode 
              ? 'Update your property details and re-run analysis calculations.'
              : 'Analyze your real estate investment opportunities with comprehensive Buy & Hold, Flip, and BRRR calculations.'
            }
          </p>
          {isEditMode && existingProperty && (
            <div className="mt-3 text-sm text-gray-500">
              Editing: {existingProperty.property_address}, {existingProperty.city}, {existingProperty.state}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            {loadingProperty ? (
              <div className="bg-white rounded-lg shadow p-8 flex justify-center">
                <LoadingSpinner size="lg" />
              </div>
            ) : !analysisResult ? (
              <PropertyAnalysisForm
                onSubmit={handleFormSubmit}
                isLoading={isSubmitting}
                initialData={existingProperty ? convertPropertyToFormData(existingProperty) : undefined}
              />
            ) : (
              <div className="space-y-6">
                {/* Results Display */}
                <AnalysisResultsDisplay result={analysisResult} />
                
                {/* Charts */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Detailed Analysis & Projections</h2>
                  <AnalysisResultsChart result={analysisResult} />
                </div>
                
                {/* Action Buttons */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setAnalysisResult(null)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                    >
                      {isEditMode ? 'Edit Property Again' : 'Analyze Another Property'}
                    </button>
                    {isEditMode && propertyId && (
                      <button
                        onClick={() => navigate(`/property/${propertyId}`)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                      >
                        View Property Details
                      </button>
                    )}
                    <button
                      onClick={() => window.print()}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                    >
                      Print Report
                    </button>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Analysis Error</h3>
                    <p className="mt-2 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tips & Guidelines */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Analysis Tips</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-2">
                  <span className="text-blue-500 mt-0.5">💡</span>
                  <p><strong>Buy & Hold:</strong> Look for properties with cap rates above 8% and positive cash flow.</p>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-green-500 mt-0.5">🔨</span>
                  <p><strong>Fix & Flip:</strong> Aim for 15%+ profit margins and keep renovation costs under 20% of ARV.</p>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-purple-500 mt-0.5">🔄</span>
                  <p><strong>BRRR:</strong> Target properties that will appraise for 75%+ more than total investment.</p>
                </div>
              </div>
            </div>

            {/* Market Assumptions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Default Assumptions</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Vacancy Rate:</span>
                  <span>5%</span>
                </div>
                <div className="flex justify-between">
                  <span>Maintenance:</span>
                  <span>10%</span>
                </div>
                <div className="flex justify-between">
                  <span>Management:</span>
                  <span>8%</span>
                </div>
                <div className="flex justify-between">
                  <span>Refinance LTV:</span>
                  <span>75%</span>
                </div>
                <div className="flex justify-between">
                  <span>Selling Costs:</span>
                  <span>6-8%</span>
                </div>
              </div>
            </div>

            {/* Recent Analyses */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Analyses</h3>
              <div className="text-sm text-gray-500">
                <p>Your recent property analyses will appear here.</p>
                <p className="mt-2">Start by analyzing your first property!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to convert PropertyResponse to PropertyAnalysisFormData
const convertPropertyToFormData = (property: PropertyResponse): Partial<PropertyAnalysisFormData> => {
  return {
    address: property.property_address,
    city: property.city || '',
    state: property.state || '',
    zipCode: property.zip_code || '',
    purchasePrice: property.purchase_price,
    downPayment: property.down_payment,
    loanAmount: property.loan_amount,
    interestRate: property.interest_rate,
    loanTerm: property.loan_term,
    annualPropertyTaxes: property.annual_taxes,
    annualInsurance: property.annual_insurance,
    monthlyHoaFees: property.monthly_hoa_fees || 0,
    expectedRentPerMonth: property.expected_monthly_rent || 0,
    afterRepairValue: property.after_repair_value || 0,
    sellingCosts: property.selling_costs || 0,
    vacancyRate: property.vacancy_rate || 5,
    maintenanceRate: property.maintenance_rate || 10,
    managementRate: property.management_rate || 8,
    analysisType: (property.analysis_status as 'buy-hold' | 'flip' | 'brrr') || 'buy-hold',
    // Renovation costs
    roofCosts: property.renovation_costs?.roof || 0,
    flooringCosts: property.renovation_costs?.flooring || 0,
    paintingCosts: property.renovation_costs?.painting || 0,
    kitchenCosts: property.renovation_costs?.kitchen || 0,
    bathroomCosts: property.renovation_costs?.bathroom || 0,
    hvacCosts: property.renovation_costs?.hvac || 0,
    electricalCosts: property.renovation_costs?.electrical || 0,
    plumbingCosts: property.renovation_costs?.plumbing || 0,
    generalFixUpCosts: property.renovation_costs?.general || 0
  };
};

// Results Display Component
const AnalysisResultsDisplay: React.FC<{ result: AnalysisResult }> = ({ result }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percent: number) => {
    return `${percent.toFixed(2)}%`;
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-xl font-bold text-gray-900 capitalize">
          {result.analysisType.replace('-', ' & ')} Analysis Results
        </h2>
        <p className="text-gray-600 mt-1">{result.propertyAddress}</p>
        <p className="text-sm text-gray-500">
          Analysis completed on {new Date(result.createdAt).toLocaleDateString()}
        </p>
      </div>

      <div className="p-6">
        {/* Buy & Hold Results */}
        {result.analysisType === 'buy-hold' && result.results && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Monthly Cash Flow</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Rent:</span>
                  <span className="font-medium">{formatCurrency(result.results.monthlyRent || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Monthly Expenses:</span>
                  <span className="font-medium text-red-600">-{formatCurrency(result.results.monthlyExpenses || 0)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Net Cash Flow:</span>
                  <span className={result.results.monthlyCashFlow && result.results.monthlyCashFlow > 0 ? 'text-green-600' : 'text-red-600'}>
                    {formatCurrency(result.results.monthlyCashFlow || 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Investment Returns</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Annual Cash Flow:</span>
                  <span className="font-medium">{formatCurrency(result.results.annualCashFlow || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cash-on-Cash Return:</span>
                  <span className="font-medium">{formatPercentage(result.results.cashOnCashReturn || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cap Rate:</span>
                  <span className="font-medium">{formatPercentage(result.results.capRate || 0)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="text-gray-600">Total Cash Invested:</span>
                  <span className="font-semibold">{formatCurrency(result.results.totalCashInvested || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Flip Results */}
        {result.analysisType === 'flip' && result.results && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Project Costs</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Project Cost:</span>
                  <span className="font-medium">{formatCurrency(result.results.totalProjectCost || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cash Invested:</span>
                  <span className="font-medium">{formatCurrency(result.results.totalCashInvested || 0)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Expected Returns</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Expected Profit:</span>
                  <span className={`font-medium ${result.results.expectedProfit && result.results.expectedProfit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(result.results.expectedProfit || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Profit Margin:</span>
                  <span className="font-medium">{formatPercentage(result.results.profitMargin || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ROI:</span>
                  <span className="font-semibold">{formatPercentage(result.results.roi || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BRRR Results */}
        {result.analysisType === 'brrr' && result.results && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Investment Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Investment:</span>
                  <span className="font-medium">{formatCurrency(result.results.totalInvestment || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Refinance Amount:</span>
                  <span className="font-medium">{formatCurrency(result.results.refinanceAmount || 0)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Cash Recovery</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cash Recovered:</span>
                  <span className="font-medium text-green-600">{formatCurrency(result.results.cashRecovered || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cash Left In:</span>
                  <span className="font-medium">{formatCurrency(result.results.cashLeftIn || 0)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="text-gray-600">Infinite Return:</span>
                  <span className={`font-semibold ${result.results.infiniteReturn ? 'text-green-600' : 'text-orange-600'}`}>
                    {result.results.infiniteReturn ? 'Yes! 🎉' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to calculate monthly mortgage payment
function calculateMonthlyPayment(loanAmount: number, annualInterestRate: number, loanTermYears: number): number {
  const monthlyInterestRate = annualInterestRate / 100 / 12;
  const numberOfPayments = loanTermYears * 12;
  
  if (monthlyInterestRate === 0) {
    return loanAmount / numberOfPayments;
  }
  
  const monthlyPayment = loanAmount * 
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / 
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  
  return Math.round(monthlyPayment);
}

export default PropertyAnalysis;
